/**
 * Month Flow — the projection engine.
 *
 * Turns three very different descriptions of "money that repeats" into one flat
 * list of dated occurrences, then buckets them into days with a running
 * balance. Everything here is pure: same inputs, same output, no Firestore, no
 * clock. That's what makes it testable (see rhythm.test.ts) and what makes the
 * "what if I cancel this" toggles instant.
 *
 * DATE HANDLING. Every date is a 'YYYY-MM-DD' string and every calculation is
 * anchored at 12:00 UTC before being sliced back to a string. Noon is far
 * enough from both midnights that no timezone or DST shift can push an instant
 * onto a neighbouring calendar day. Adelaide runs a HALF-HOUR offset
 * (+9:30/+10:30), which is exactly what naive local-Date arithmetic gets wrong.
 * Do not "simplify" this to local Dates or to `new Date('YYYY-MM-DD')` (that
 * parses as UTC midnight, which is the previous day in the Americas and gets
 * the day wrong the moment anyone opens this on a trip).
 *
 * MONTH STEPS are anchor-clamped, never rolled over: a 31st item goes
 * Jan-31 → Feb-28 → Mar-31. `Date.setMonth()` would give Mar-3 and skip
 * February entirely. This mirrors `addFrequency` in utils/recurrence.ts, which
 * is itself a faithful port of the backend scheduler.
 */

import { RepeatingInvoice } from '../../../types/accounting';
import { CrmSubscription } from '../../../contexts/SubscriptionsContext';
import { addFrequency, daysInMonth, normalizeFrequency } from '../../../utils/recurrence';
import {
  Certainty,
  MoneySource,
  RecurringMoneyItem,
  RhythmDay,
  RhythmFilters,
  RhythmOccurrence,
  RhythmResult,
  RhythmScenario,
  TxScope,
} from '../types';

// ---------------------------------------------------------------------------
// Date helpers — all 'YYYY-MM-DD' in, all 'YYYY-MM-DD' out
// ---------------------------------------------------------------------------

const DAY_MS = 86400000;
const MAX_OCCURRENCES_PER_SOURCE = 400; // ~13 months of daily drip; a runaway guard, not a real limit

/** Is this a well-formed 'YYYY-MM-DD'? Rejects '2026-13-01' and '2026-02-31'. */
export function isDayString(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number);
  if (m < 1 || m > 12) return false;
  return d >= 1 && d <= daysInMonth(y, m - 1);
}

/** Coerce anything date-ish to 'YYYY-MM-DD', or null. Accepts a bare day, a
 *  full ISO instant, a Date, or a Firestore Timestamp. */
export function toDay(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === 'string') {
    if (isDayString(value)) return value;
    // A full ISO instant: take the UTC day. Our own writers emit UTC-midnight
    // for all-day values, which is the same calendar day in Adelaide.
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString().slice(0, 10);
  }
  if (typeof value === 'object' && typeof (value as any).toDate === 'function') {
    try {
      return toDay((value as any).toDate());
    } catch {
      return null;
    }
  }
  return null;
}

function noon(day: string): Date {
  return new Date(`${day}T12:00:00Z`);
}

export function addDays(day: string, n: number): string {
  return new Date(noon(day).getTime() + n * DAY_MS).toISOString().slice(0, 10);
}

/** Whole days from a to b (b − a). Negative when b precedes a. */
export function dayDiff(a: string, b: string): number {
  return Math.round((noon(b).getTime() - noon(a).getTime()) / DAY_MS);
}

/** 0 = Sunday … 6 = Saturday. */
export function weekdayOf(day: string): number {
  return noon(day).getUTCDay();
}

/** Months since year 0 — lets us compare and step months without Date maths. */
function monthIndex(day: string): number {
  const [y, m] = day.split('-').map(Number);
  return y * 12 + (m - 1);
}

/** Build a day string from an absolute month index, clamping the day-of-month
 *  to the target month's length. This is the anti-rollover rule. */
function dayInMonth(absMonth: number, dayOfMonth: number): string {
  const y = Math.floor(absMonth / 12);
  const m0 = absMonth % 12;
  const d = Math.min(Math.max(1, dayOfMonth), daysInMonth(y, m0));
  return `${String(y).padStart(4, '0')}-${String(m0 + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** The inverse of `addFrequency` — one period BACKWARDS, same anchor clamp.
 *
 *  Needed because templates only store `nextDate` (the next unbilled cycle).
 *  Looking at the current month halfway through it, the occurrence that already
 *  happened on the 1st is real money that left the account, and a month view
 *  that hides it is simply wrong. Because both directions clamp against the
 *  same anchor day, walking back N periods and forward N periods round-trips
 *  exactly. */
export function subFrequency(dateStr: string, frequency: string, anchorDateStr: string): string {
  const freq = normalizeFrequency(frequency);
  if (freq === 'WEEKLY') return addDays(dateStr, -7);
  const stepMonths = freq === 'MONTHLY' ? 1 : freq === 'QUARTERLY' ? 3 : 12;
  const anchorDay = Number((anchorDateStr || dateStr).split('-')[2]);
  return dayInMonth(monthIndex(dateStr) - stepMonths, anchorDay);
}

/** Every day from start to end inclusive. */
export function eachDay(start: string, end: string): string[] {
  const out: string[] = [];
  if (dayDiff(start, end) < 0) return out;
  let cur = start;
  // dayDiff bounds the loop, so a malformed input can't spin forever.
  const span = dayDiff(start, end);
  for (let i = 0; i <= span; i++) {
    out.push(cur);
    cur = addDays(cur, 1);
  }
  return out;
}

/** First and last day of the month containing `day`. */
export function monthBounds(day: string): { start: string; end: string } {
  const [y, m] = day.split('-').map(Number);
  return {
    start: `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-01`,
    end: dayInMonth(monthIndex(day), 31),
  };
}

// ---------------------------------------------------------------------------
// Amount selection
// ---------------------------------------------------------------------------

/** Which figure this scenario uses.
 *
 *  `expectedAmount` of 0 is meaningful — the household sheet uses it for bills
 *  that exist but aren't due this cycle — so this tests `undefined`, never
 *  falsiness. `?? amount` would be right too; `=== undefined` says why. */
export function amountFor(item: RecurringMoneyItem, scenario: RhythmScenario): number {
  if (scenario === 'expected' && item.expectedAmount !== undefined) {
    return item.expectedAmount;
  }
  return item.amount;
}

// ---------------------------------------------------------------------------
// Source 1 — manual recurring items
// ---------------------------------------------------------------------------

/** Clip a projection window to the item's own start/end bounds. Returns null
 *  when the item doesn't overlap the window at all. */
function clipWindow(
  item: RecurringMoneyItem,
  rangeStart: string,
  rangeEnd: string
): { start: string; end: string } | null {
  let start = rangeStart;
  let end = rangeEnd;
  const itemStart = item.startDate && isDayString(item.startDate) ? item.startDate : null;
  const itemEnd = item.endDate && isDayString(item.endDate) ? item.endDate : null;
  if (itemStart && itemStart > start) start = itemStart;
  if (itemEnd && itemEnd < end) end = itemEnd;
  return dayDiff(start, end) < 0 ? null : { start, end };
}

/**
 * Project one manual item across a window.
 *
 * The amount is resolved ONCE up front so a scenario switch can never produce
 * a mix of full and expected figures within a single item.
 */
export function projectItem(
  item: RecurringMoneyItem,
  rangeStart: string,
  rangeEnd: string,
  scenario: RhythmScenario
): RhythmOccurrence[] {
  const window = clipWindow(item, rangeStart, rangeEnd);
  if (!window) return [];
  const amount = amountFor(item, scenario);

  // A zero-value item still exists as a record, but drawing it would put
  // meaningless rows on every day of the chart.
  if (!(amount > 0)) return [];

  const out: RhythmOccurrence[] = [];
  const base = {
    label: item.name,
    direction: item.direction,
    source: 'manual' as MoneySource,
    sourceId: item.id,
    category: item.category,
    scope: item.scope,
  };

  const push = (date: string, amt: number, certainty: Certainty, detail: string, isSpread?: boolean) => {
    if (out.length >= MAX_OCCURRENCES_PER_SOURCE) return;
    out.push({
      id: `manual:${item.id}:${date}`,
      date,
      amount: amt,
      certainty,
      detail,
      ...(isSpread ? { isSpread: true } : {}),
      ...base,
    });
  };

  switch (item.kind) {
    case 'one_off': {
      const d = item.anchorDate && isDayString(item.anchorDate) ? item.anchorDate : null;
      if (d && d >= window.start && d <= window.end) push(d, amount, 'expected', 'One-off');
      break;
    }

    case 'monthly_day': {
      const dom = item.dayOfMonth || 1;
      const firstMonth = monthIndex(window.start);
      const lastMonth = monthIndex(window.end);
      for (let m = firstMonth; m <= lastMonth; m++) {
        const d = dayInMonth(m, dom);
        if (d >= window.start && d <= window.end) {
          push(d, amount, 'expected', `Monthly on the ${ordinal(dom)}`);
        }
      }
      break;
    }

    case 'yearly': {
      const dom = item.dayOfMonth || 1;
      const mon = item.month || 1;
      const firstYear = Number(window.start.slice(0, 4));
      const lastYear = Number(window.end.slice(0, 4));
      for (let y = firstYear; y <= lastYear; y++) {
        const d = dayInMonth(y * 12 + (mon - 1), dom);
        if (d >= window.start && d <= window.end) {
          push(d, amount, 'expected', `Yearly on ${ordinal(dom)} ${MONTH_NAMES[mon - 1]}`);
        }
      }
      break;
    }

    case 'weekly': {
      const target = item.weekday ?? 1;
      // Walk to the first matching weekday on or after the window start.
      const offset = (target - weekdayOf(window.start) + 7) % 7;
      let cur = addDays(window.start, offset);
      while (cur <= window.end && out.length < MAX_OCCURRENCES_PER_SOURCE) {
        push(cur, amount, 'expected', `Every ${DAY_NAMES[target]}`);
        cur = addDays(cur, 7);
      }
      break;
    }

    case 'fortnightly': {
      // The anchor is one known occurrence; the series runs both ways from it,
      // so a fortnightly item entered with last payday's date immediately
      // projects the right days going forward.
      const anchor = item.anchorDate && isDayString(item.anchorDate) ? item.anchorDate : window.start;
      const gap = dayDiff(anchor, window.start);
      let cur = addDays(anchor, Math.ceil(gap / 14) * 14);
      while (cur <= window.end && out.length < MAX_OCCURRENCES_PER_SOURCE) {
        if (cur >= window.start) push(cur, amount, 'expected', 'Every 2 weeks');
        cur = addDays(cur, 14);
      }
      break;
    }

    case 'quarterly': {
      const anchor = item.anchorDate && isDayString(item.anchorDate) ? item.anchorDate : window.start;
      const anchorDom = Number(anchor.split('-')[2]);
      const anchorMonth = monthIndex(anchor);
      const startMonth = monthIndex(window.start);
      // Step in whole quarters from the anchor, forwards or backwards.
      const quartersToStart = Math.floor((startMonth - anchorMonth) / 3);
      let m = anchorMonth + quartersToStart * 3;
      while (m <= monthIndex(window.end) && out.length < MAX_OCCURRENCES_PER_SOURCE) {
        const d = dayInMonth(m, anchorDom);
        if (d >= window.start && d <= window.end) push(d, amount, 'expected', 'Every 3 months');
        m += 3;
      }
      break;
    }

    case 'monthly_spread': {
      // No date at all — groceries, fuel. Pinning the whole month's spend to a
      // single day would carve a cliff into the balance curve that doesn't
      // exist, and the lowest-balance figure is the main reason to draw the
      // curve at all. So drip it: each day carries amount / (days in THAT
      // month). A part-month window therefore correctly costs part of the
      // budget.
      for (const d of eachDay(window.start, window.end)) {
        const [y, m] = d.split('-').map(Number);
        const perDay = amount / daysInMonth(y, m - 1);
        push(d, perDay, 'estimated', 'Spread across the month', true);
      }
      break;
    }

    default:
      break;
  }

  return out;
}

// ---------------------------------------------------------------------------
// Source 2 — repeating invoice / bill templates
// ---------------------------------------------------------------------------

/** Template total: stored when the backend computed it, else derived. Mirrors
 *  `repeatingInvoiceToCalendarItems` so the two surfaces can't disagree. */
export function repeatingTemplateTotal(t: RepeatingInvoice): number {
  if (typeof t.total === 'number' && !Number.isNaN(t.total)) return t.total;
  return (t.lineItems || []).reduce(
    (sum, li) => sum + (li.lineAmount ?? (li.quantity ?? 1) * (li.unitAmount ?? 0)),
    0
  );
}

/**
 * Project a repeating invoice/bill template.
 *
 * Walks BACKWARDS from `nextDate` to cover the start of the window before
 * walking forward. Templates only record the next unbilled cycle, so on the
 * 13th of the month the 1st's invoice is already raised and `nextDate` points
 * at next month — projecting forward only would show an August with a hole in
 * it. Back-walk stops at the template's own `startDate`.
 */
export function projectRepeatingInvoice(
  template: RepeatingInvoice,
  rangeStart: string,
  rangeEnd: string
): RhythmOccurrence[] {
  if (template.status !== 'ACTIVE') return [];
  const next = toDay(template.nextDate);
  if (!next) return [];

  let frequency: string;
  try {
    frequency = normalizeFrequency(template.frequency);
  } catch {
    // The backend PAUSES a template it can't schedule rather than guessing.
    // The forecast's equivalent is to predict nothing, not to invent monthly
    // occurrences that will never be billed.
    return [];
  }

  const total = repeatingTemplateTotal(template);
  if (!(total > 0)) return [];

  const isBill = template.templateType === 'bill';
  const anchor = toDay(template.scheduleAnchorDate) || toDay(template.startDate) || next;
  const templateEnd = toDay(template.endDate);

  // Back-walk to a seed at or before the window start.
  let seed = next;
  for (let i = 0; i < MAX_OCCURRENCES_PER_SOURCE && seed > rangeStart; i++) {
    const prev = subFrequency(seed, frequency, anchor);
    if (!prev || prev >= seed) break;      // non-advancing cursor guard
    if (prev < anchor) break;              // never before the template existed
    seed = prev;
  }

  const out: RhythmOccurrence[] = [];
  let cur = seed;
  for (let i = 0; i < MAX_OCCURRENCES_PER_SOURCE && cur <= rangeEnd; i++) {
    if (templateEnd && cur > templateEnd) break;
    if (cur >= rangeStart) {
      out.push({
        id: `${isBill ? 'repeating_bill' : 'repeating_invoice'}:${template.id}:${cur}`,
        date: cur,
        label: `${isBill ? 'Bill' : 'Invoice'}: ${template.contactName || 'Unknown'}`,
        amount: total,
        direction: isBill ? 'out' : 'in',
        source: isBill ? 'repeating_bill' : 'repeating_invoice',
        sourceId: template.id,
        category: isBill ? 'expense:supplier' : 'income:customer_payment',
        // Business templates are business books by definition — this page's
        // scope filter is about which set of books, not which bank account.
        scope: 'business',
        // Everything here is billed by a template we control, so the date is as
        // good as our own scheduler; only the FIRST unbilled one is "next".
        certainty: cur < next ? 'confirmed' : 'expected',
        counterparty: template.contactName || undefined,
        detail: `${titleCase(frequency)} · ${template.reference || 'recurring template'}`,
      });
    }
    const nextDay = addFrequency(cur, frequency, anchor);
    if (!nextDay || nextDay <= cur) break;
    cur = nextDay;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Source 3 — Stripe subscriptions
// ---------------------------------------------------------------------------

/** Stripe intervals we can project. Anything else (daily plans) is skipped
 *  rather than guessed at. */
function stripeStep(interval: string, count: number): { unit: 'day' | 'month'; size: number } | null {
  const i = String(interval || '').toLowerCase();
  const n = Math.max(1, Number(count) || 1);
  if (i === 'month') return { unit: 'month', size: n };
  if (i === 'year') return { unit: 'month', size: 12 * n };
  if (i === 'week') return { unit: 'day', size: 7 * n };
  return null;
}

/**
 * Project a Stripe subscription's charge dates.
 *
 * Stripe tells us exactly when the next charge lands (`currentPeriodEnd`), so
 * that one is a fact; everything after it is our own arithmetic. Test-mode
 * subscriptions are excluded outright — they are not money.
 *
 * Tiered deals step down: `$350/mo for 12 months, then $90/mo`. Once the agreed
 * cycles are used up the projection switches to the phase-2 figure, because
 * forecasting twelve more months at the opening rate would overstate income by
 * thousands.
 */
export function projectSubscription(
  sub: CrmSubscription,
  rangeStart: string,
  rangeEnd: string
): RhythmOccurrence[] {
  if (sub.testMode) return [];
  if (sub.linkStatus === 'dismissed') return [];
  if (sub.status === 'canceled' || sub.endedAt) return [];

  const step = stripeStep(sub.interval, sub.intervalCount);
  if (!step) return [];

  const nextCharge = toDay(sub.currentPeriodEnd);
  if (!nextCharge) return [];

  // cancel_at_period_end means the plan STOPS at the end of the paid period —
  // there is no charge then, and none after. Treating it as "one more payment"
  // would book revenue that will never arrive.
  if (sub.cancelAtPeriodEnd) return [];

  const hardEnd = toDay(sub.cancelAt) || toDay(sub.scheduledEndsAt);
  const openingAmount = Number(sub.currentAmount) || 0;
  const stepDownAmount = sub.phase2AmountIncGst ?? null;
  const termCycles = sub.termCycles ?? sub.initialTermIterations ?? null;
  const paidSoFar = Number(sub.paymentsMade) || 0;
  // Cycles left at the opening rate, counted from the next charge.
  const cyclesAtOpeningRate =
    termCycles !== null && stepDownAmount !== null ? Math.max(0, termCycles - paidSoFar) : null;

  const advance = (day: string, dir: 1 | -1): string =>
    step.unit === 'day'
      ? addDays(day, dir * step.size)
      : dayInMonth(monthIndex(day) + dir * step.size, Number(nextCharge.split('-')[2]));

  // Back-walk so a part-elapsed month still shows the charge that already hit.
  const subStart = toDay(sub.createdAt) || null;
  let seed = nextCharge;
  let backSteps = 0;
  for (let i = 0; i < MAX_OCCURRENCES_PER_SOURCE && seed > rangeStart; i++) {
    const prev = advance(seed, -1);
    if (!prev || prev >= seed) break;
    if (subStart && prev < subStart) break;
    seed = prev;
    backSteps++;
  }

  const out: RhythmOccurrence[] = [];
  let cur = seed;
  // Cycle 0 == the seed. Forward cycles past `nextCharge` are indexed from it.
  for (let i = 0; i < MAX_OCCURRENCES_PER_SOURCE && cur <= rangeEnd; i++) {
    if (hardEnd && cur > hardEnd) break;
    if (cur >= rangeStart) {
      const forwardIndex = i - backSteps; // 0 at nextCharge, negative in the past
      const steppedDown =
        cyclesAtOpeningRate !== null && forwardIndex >= cyclesAtOpeningRate;
      const amount = steppedDown
        ? (stepDownAmount as number)
        : forwardIndex < 0
        ? sub.lastPaymentAmount ?? openingAmount
        : openingAmount;
      if (amount > 0) {
        out.push({
          id: `stripe_subscription:${sub.id}:${cur}`,
          date: cur,
          label: `${sub.linkedCustomerName || sub.customerName || 'Stripe plan'}`,
          amount,
          direction: 'in',
          source: 'stripe_subscription',
          sourceId: sub.id,
          category: 'income:customer_payment',
          scope: 'business',
          // Stripe published this exact date for the next charge; the rest is
          // our extrapolation.
          certainty: forwardIndex <= 0 ? 'confirmed' : 'estimated',
          counterparty: sub.linkedCustomerName || sub.customerName || undefined,
          detail: [
            sub.planName || 'Subscription',
            `every ${sub.intervalCount > 1 ? `${sub.intervalCount} ` : ''}${sub.interval}`,
            steppedDown ? 'stepped-down rate' : null,
          ]
            .filter(Boolean)
            .join(' · '),
        });
      }
    }
    const nextDay = advance(cur, 1);
    if (!nextDay || nextDay <= cur) break;
    cur = nextDay;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Compose + filter + bucket
// ---------------------------------------------------------------------------

export interface RhythmInputs {
  bills?: Array<{ id: string; repeatingTemplateId?: string; date: string; dueDate: string; status: string; amountDue: number; contactName: string | null; billNumber: string }>;
  asOf?: string;
  items: RecurringMoneyItem[];
  templates: RepeatingInvoice[];
  subscriptions: CrmSubscription[];
}

/** An empty filter set means "no filter", NOT "match nothing" — a set that
 *  starts empty is how the UI expresses "all", and every chip the user unticks
 *  should narrow rather than blank the page. */
function passes<T>(set: Set<T> | undefined, value: T): boolean {
  return !set || set.size === 0 || set.has(value);
}

/**
 * The one function the view calls. Projects everything, applies filters, then
 * buckets into days with a running balance.
 */
export function buildRhythm(inputs: RhythmInputs, filters: RhythmFilters): RhythmResult {
  const { rangeStart, rangeEnd } = filters;
  const occurrences: RhythmOccurrence[] = [];

  if (isDayString(rangeStart) && isDayString(rangeEnd) && dayDiff(rangeStart, rangeEnd) >= 0) {
    for (const item of inputs.items) {
      if (!item.active) continue;
      if (filters.mutedIds.has(item.id)) continue;
      occurrences.push(...projectItem(item, rangeStart, rangeEnd, filters.scenario));
    }
    for (const t of inputs.templates) {
      if (filters.mutedIds.has(t.id)) continue;
      const projected = projectRepeatingInvoice(t, rangeStart, rangeEnd);
      // Once real bills are loaded, only ungenerated cycles remain projections.
      occurrences.push(...projected.filter(o => t.templateType !== 'bill' || !inputs.bills || (o.date >= t.nextDate && !inputs.bills.some(b => b.repeatingTemplateId === t.id && b.date === o.date))));
    }
    for (const b of inputs.bills || []) {
      if (!['APPROVED', 'SENT'].includes(b.status) || !(b.amountDue > 0) || (b.repeatingTemplateId && filters.mutedIds.has(b.repeatingTemplateId))) continue;
      // Past-due unpaid bills remain a current obligation, never silently disappear.
      const due = toDay(b.dueDate) || toDay(b.date);
      const date = due ? [due, inputs.asOf || due, rangeStart].sort().pop()! : null;
      if (!date || date < rangeStart || date > rangeEnd) continue;
      occurrences.push({ id: 'bill:' + b.id, date, label: b.billNumber + ': ' + b.contactName, amount: b.amountDue, direction: 'out', source: 'bill', sourceId: b.id, category: 'expense:supplier', scope: 'business', certainty: 'expected', counterparty: b.contactName || undefined, detail: due && due < date ? 'Overdue · remaining balance' : 'Existing bill · remaining balance' });
    }
    for (const s of inputs.subscriptions) {
      if (filters.mutedIds.has(s.id)) continue;
      occurrences.push(...projectSubscription(s, rangeStart, rangeEnd));
    }
  }

  const needle = filters.search.trim().toLowerCase();
  const kept = occurrences.filter((o) => {
    if (!passes(filters.directions, o.direction)) return false;
    if (!passes(filters.scopes, o.scope)) return false;
    if (!passes(filters.sources, o.source)) return false;
    if (!passes(filters.categories, o.category)) return false;
    if (needle) {
      const hay = `${o.label} ${o.counterparty || ''} ${o.detail || ''}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });

  kept.sort((a, b) => (a.date === b.date ? b.amount - a.amount : a.date < b.date ? -1 : 1));

  const byDate = new Map<string, RhythmOccurrence[]>();
  for (const o of kept) {
    const list = byDate.get(o.date);
    if (list) list.push(o);
    else byDate.set(o.date, [o]);
  }

  const days: RhythmDay[] = [];
  let balance = filters.openingBalance;
  let totalIn = 0;
  let totalOut = 0;
  let lowestBalance: number | null = null;
  let lowestBalanceDate: string | null = null;

  const span =
    isDayString(rangeStart) && isDayString(rangeEnd) ? eachDay(rangeStart, rangeEnd) : [];

  for (const date of span) {
    const list = byDate.get(date) || [];
    let inflow = 0;
    let outflow = 0;
    for (const o of list) {
      if (o.direction === 'in') inflow += o.amount;
      else outflow += o.amount;
    }
    totalIn += inflow;
    totalOut += outflow;
    balance += inflow - outflow;
    days.push({ date, inflow, outflow, net: inflow - outflow, balance, occurrences: list });
    if (lowestBalance === null || balance < lowestBalance) {
      lowestBalance = balance;
      lowestBalanceDate = date;
    }
  }

  // Category and source rollups. Direction is part of the key so a category
  // that carries both (a customer refund under customer_payment) never nets
  // itself into an invisible zero.
  const catMap = new Map<string, { category: string; direction: 'in' | 'out'; total: number; count: number }>();
  const srcMap = new Map<string, { source: MoneySource; direction: 'in' | 'out'; total: number; count: number }>();
  for (const o of kept) {
    const ck = `${o.category}|${o.direction}`;
    const c = catMap.get(ck) || { category: o.category, direction: o.direction, total: 0, count: 0 };
    c.total += o.amount;
    c.count += 1;
    catMap.set(ck, c);

    const sk = `${o.source}|${o.direction}`;
    const s = srcMap.get(sk) || { source: o.source, direction: o.direction, total: 0, count: 0 };
    s.total += o.amount;
    s.count += 1;
    srcMap.set(sk, s);
  }

  return {
    days,
    occurrences: kept,
    totalIn,
    totalOut,
    net: totalIn - totalOut,
    openingBalance: filters.openingBalance,
    closingBalance: balance,
    lowestBalance,
    lowestBalanceDate,
    byCategory: Array.from(catMap.values()).sort((a, b) => b.total - a.total),
    bySource: Array.from(srcMap.values()).sort((a, b) => b.total - a.total),
  };
}

/**
 * What one item costs over a whole month, normalised.
 *
 * Used by the item list so a weekly $26.53 and a monthly $106 can be compared
 * on the same line — the household sheet counts "Every Friday" once, which
 * quietly understates it by 3–4×. Yearly items are shown at 1/12 for the same
 * reason.
 */
export function monthlyEquivalent(item: RecurringMoneyItem, scenario: RhythmScenario): number {
  const amount = amountFor(item, scenario);
  switch (item.kind) {
    case 'weekly':
      return (amount * 52) / 12;
    case 'fortnightly':
      return (amount * 26) / 12;
    case 'monthly_day':
    case 'monthly_spread':
      return amount;
    case 'quarterly':
      return amount / 3;
    case 'yearly':
      return amount / 12;
    case 'one_off':
      return 0; // doesn't recur, so it has no monthly rate
    default:
      return amount;
  }
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/** Human summary of a schedule, for the item list. */
export function describeSchedule(item: RecurringMoneyItem): string {
  switch (item.kind) {
    case 'monthly_day':
      return `Monthly · ${ordinal(item.dayOfMonth || 1)}`;
    case 'monthly_spread':
      return 'Monthly · spread';
    case 'weekly':
      return `Weekly · ${DAY_NAMES[item.weekday ?? 1]}`;
    case 'fortnightly':
      return `Fortnightly${item.anchorDate ? ` · from ${item.anchorDate}` : ''}`;
    case 'quarterly':
      return `Quarterly${item.anchorDate ? ` · from ${item.anchorDate}` : ''}`;
    case 'yearly':
      return `Yearly · ${ordinal(item.dayOfMonth || 1)} ${MONTH_NAMES[(item.month || 1) - 1]}`;
    case 'one_off':
      return `One-off${item.anchorDate ? ` · ${item.anchorDate}` : ''}`;
    default:
      return 'Recurring';
  }
}

export const SOURCE_LABELS: Record<MoneySource, string> = {
  bill: 'Unpaid bills',
  manual: 'Personal & manual',
  repeating_invoice: 'Recurring invoices',
  repeating_bill: 'Recurring bills',
  stripe_subscription: 'Stripe plans',
};

export const SOURCE_COLOURS: Record<MoneySource, string> = {
  bill: '#e36a76',
  manual: '#f97316',
  repeating_invoice: '#10b981',
  repeating_bill: '#ef4444',
  stripe_subscription: '#6366f1',
};

export const SCOPE_LABELS: Record<TxScope, string> = {
  business: 'Business',
  personal: 'Family',
};
